import React, {
  RefObject,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import cn from 'classnames';
import {
  ReactZoomPanPinchRef,
  ReactZoomPanPinchState,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import { useReadLocalStorage } from 'usehooks-ts';

import loader from 'assets/images/loading.gif';
import { ReturnComponentType } from 'common';
import { ModalShowScreen, NotifyComponent, VideoSettings } from 'components';
import { MapSize, NotifyType, ProcessingStatus } from 'enums';
import { useAppSelector, useClickOutside, useScreenshot } from 'hooks';
import { VideoPlayerHookType } from 'hooks/useVideoPlayer/useVideoPlayer';
import { FileType, TimeCodeType, selectFiles, selectIsPlaying } from 'state';
import './VideoPlayer.scss';

/**
 * How far past a failed (corrupt) spot to seek on each recovery attempt.
 * Seeking rewinds to the previous keyframe and decodes forward, so a skip
 * smaller than the keyframe interval re-decodes the corrupt packets and fails
 * again — later attempts must jump well past the damage.
 */
const RECOVERY_SKIP_SCHEDULE_SECONDS = [2, 5, 10, 30];
/** failures within this many seconds of the last one count as the same failure point */
const SAME_FAILURE_WINDOW_SECONDS = 35;
/** once playback gets this far past a failure point, consider it cleared and reset the retry schedule */
const RECOVERY_CLEARED_SECONDS = 5;

type VideoPlayerType = {
  videoRef: RefObject<HTMLVideoElement>;
  className?: string;
  focusedTimeCode?: TimeCodeType;
  timeCodes: TimeCodeType[];
  videoPlayer: VideoPlayerHookType;
};

export const VideoPlayer = ({
  videoRef,
  className,
  focusedTimeCode,
  timeCodes,
  videoPlayer,
}: VideoPlayerType): ReturnComponentType => {
  const files = useAppSelector(selectFiles);
  const isPlaying = useAppSelector(selectIsPlaying);
  let transformFn:
    | ((newPositionX: number, newPositionY: number, newScale: number) => void)
    | null;
  const mapSize = useReadLocalStorage('map:mapSize');
  const [defaultScale, setDefaultScale] = useState(1);
  const [isLoading, setLoading] = useState(true);
  const [notification, setNotification] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const {
    currentFile,
    togglePlay,
    handleManualInspectionProgress,
    inspectionProgress,
    advanceVideo,
    isVideoLoaded,
    setVideoLoaded,
    fileProgress,
    setFileProgress,
    goToNextVideo,
    notification: videoPlayerNotification,
  } = videoPlayer;
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);

  const [showVideoSettings, setShowVideoSettings] = useState(false);
  const [brightness, setBrightness] = useState(1);
  const [contrast, setContrast] = useState(1);

  const onClickOutside = (target: HTMLDivElement) => {
    if (!target.classList.contains('VideoSettingsButton')) {
      setShowVideoSettings(false);
    }
  };

  /** preload the current video playing, as well as the video before and after it, but not the other videos */
  const getPreload = (fileId: string) => {
    const currentFileIndex = files.findIndex(f => f.id === (currentFile?.id || ''));

    const fileIndex = files.findIndex(f => f.id === fileId);

    if (currentFileIndex < 0 || fileIndex < 0) return 'none';

    if (fileIndex >= currentFileIndex - 1 && fileIndex <= currentFileIndex + 1)
      return 'auto'; // preload video

    return 'metadata'; // preload only metadata
  };

  useClickOutside(settingsRef, onClickOutside);

  const { image, snap, getZoomState, zoomState } = useScreenshot(
    videoRef,
    setShowScreenshotModal,
    containerRef,
    brightness,
    contrast,
  );

  const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);

  const goToPreviousTimecode = useCallback(() => {
    const prevTimecode = timeCodes
      .reverse()
      .find(
        t => Math.round(t.inspectionTimecodeStart || 0) < Math.round(inspectionProgress),
      );

    if (prevTimecode === undefined) return;

    handleManualInspectionProgress(prevTimecode.inspectionTimecodeStart || 0);
  }, [handleManualInspectionProgress, inspectionProgress, timeCodes]);

  const goToNextTimecode = useCallback(() => {
    const nextTimecode = timeCodes.find(
      t => Math.round(t.inspectionTimecodeStart || 0) > Math.round(inspectionProgress),
    );

    if (nextTimecode === undefined) return;

    handleManualInspectionProgress(nextTimecode.inspectionTimecodeStart || 0);
  }, [handleManualInspectionProgress, inspectionProgress, timeCodes]);

  const handleScaleChange = (e: ReactZoomPanPinchRef) => {
    getZoomState(e.state);
  };

  const keyListener = useCallback(
    (e: KeyboardEvent) => {
      const activeElement = document.activeElement?.tagName.toLowerCase();

      if (e.code === 'Space' && !e.repeat) {
        // if there is a focused element, don't play (unless it's the play button)
        if (activeElement !== 'button' && activeElement !== 'input') {
          togglePlay();
        }
      }

      // These are flpped because the client want the left arrow to move the video to the right and vice versa
      if (e.code === 'ArrowLeft') {
        e.preventDefault();
        advanceVideo(e.shiftKey ? 1 : 0.125);
      }
      if (e.code === 'ArrowRight') {
        e.preventDefault();
        advanceVideo(e.shiftKey ? -1 : -0.125);
      }
      if (e.code === 'ArrowUp') {
        e.preventDefault();
        goToPreviousTimecode();
      }
      if (e.code === 'ArrowDown') {
        e.preventDefault();
        goToNextTimecode();
      }

      if (activeElement !== 'input' && e.key === 's' && isVideoLoaded) {
        snap();
      }

      if (showVideoSettings && e.key === 'Escape') {
        setShowVideoSettings(false);
      }
    },
    [
      advanceVideo,
      togglePlay,
      goToPreviousTimecode,
      goToNextTimecode,
      isVideoLoaded,
      zoomState,
      showVideoSettings,
    ],
  );

  useEffect(() => {
    document.addEventListener('keydown', keyListener);

    return () => {
      document.removeEventListener('keydown', keyListener);
    };
  }, [keyListener]);

  useEffect(() => {
    // when the video container gets resized for any reason, recenter & rezoom the video
    // NOTE: the video zoom should not change when the play button is pressed or video progress is changed
    const resizeObserver = new ResizeObserver(() => {
      const containerRect = containerRef.current?.getBoundingClientRect();

      if (videoRef.current && containerRect && transformFn && isVideoLoaded) {
        const newScale = Math.min(
          containerRect.width / videoRef.current.videoWidth,
          containerRect.height / videoRef.current.videoHeight,
        );

        const newX = (containerRect.width - videoRef.current.videoWidth * newScale) / 2;
        const newY = (containerRect.height - videoRef.current.videoHeight * newScale) / 2;

        if (Number.isNaN(newX) || Number.isNaN(newY) || Number.isNaN(newScale)) return;

        // use the 'setTransform' function from the react-zoom-pan-pinch commponent
        transformFn(newX, newY, newScale);
        setDefaultScale(newScale);
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);

      return () => resizeObserver.disconnect();
    }
  }, [containerRef, mapSize, transformComponentRef, isVideoLoaded]);

  // decide if video file is ready to play or not, and broadcast that to the hook
  useEffect(() => {
    if (
      videoRef.current &&
      videoRef.current.duration &&
      videoRef.current.readyState >= videoRef.current.HAVE_METADATA
    ) {
      setVideoLoaded(true);
    } else {
      setVideoLoaded(false);
    }
  }, [
    videoRef?.current,
    videoRef.current?.duration,
    videoRef.current?.readyState,
    videoRef.current?.HAVE_METADATA,
  ]);

  // if the video is set to play via the hook, start it playing here
  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    if (isPlaying && video.paused) {
      // play() returns a promise that rejects if playback is interrupted before
      // it starts — e.g. when navigating away unmounts the <video> element. That
      // AbortError is benign; swallow it so it doesn't surface as a runtime error.
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch((error: DOMException) => {
          if (error?.name !== 'AbortError') {
            console.error('Video play failed:', error);
          }
        });
      }
    }
    if (!isPlaying && !video.paused) {
      video.pause();
    }
  }, [currentFile, isPlaying]);

  // when the playback jumps to a new video, set it to the correct spot
  useEffect(() => {
    if (!videoRef.current) return;
    // eslint-disable-next-line no-param-reassign
    videoRef.current.currentTime = fileProgress;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [currentFile?.id]);

  // tracks attempts to skip past a corrupt section of the current video
  const recoveryRef = useRef({ fileId: '', failTime: 0, attempts: 0 });

  /**
   * Some videos have corrupt packets partway through. Desktop players skip them
   * and keep going, but the browser's decoder raises a fatal MEDIA_ERR_DECODE and
   * stops. Recover by reloading the element and seeking just past the bad spot,
   * skipping further on each retry. Give up (and show the error toast) after
   * MAX_RECOVERY_ATTEMPTS at the same spot, or for non-decode errors.
   */
  const handleVideoError = (e: SyntheticEvent<HTMLVideoElement>, file: FileType) => {
    const video = e.currentTarget;
    const mediaError = video.error;
    const failTime = Math.max(video.currentTime || 0, fileProgress || 0);

    // eslint-disable-next-line no-console
    console.error(
      `Video error: code=${mediaError?.code} message="${mediaError?.message}" file=${file.id} t=${failTime}`,
    );

    const recovery = recoveryRef.current;
    const isSameFailurePoint =
      recovery.fileId === file.id &&
      Math.abs(failTime - recovery.failTime) < SAME_FAILURE_WINDOW_SECONDS;

    const canRecover =
      mediaError?.code === MediaError.MEDIA_ERR_DECODE &&
      failTime > 0 &&
      (!isSameFailurePoint || recovery.attempts < RECOVERY_SKIP_SCHEDULE_SECONDS.length);

    if (!canRecover) {
      setNotification('An error has occurred, and the video is unable to load.');

      return;
    }

    recovery.fileId = file.id;
    recovery.attempts = isSameFailurePoint ? recovery.attempts + 1 : 1;
    recovery.failTime = isSameFailurePoint ? recovery.failTime : failTime;

    const target =
      recovery.failTime + RECOVERY_SKIP_SCHEDULE_SECONDS[recovery.attempts - 1];
    const wasPlaying = isPlaying;

    // if the corruption is at the very end of the file, move on to the next one
    if (video.duration && target >= video.duration) {
      goToNextVideo(wasPlaying);

      return;
    }

    // eslint-disable-next-line no-console
    console.warn(`Attempting recovery: seeking to t=${target} in file=${file.id}`);

    const onLoaded = () => {
      video.currentTime = target;
      setFileProgress(target);
      if (wasPlaying) {
        video.play().catch(() => {});
      }
    };

    video.addEventListener('loadedmetadata', onLoaded, { once: true });
    video.load();
  };

  const onTimeUpdate = (e: SyntheticEvent<HTMLVideoElement>) => {
    if (!videoRef.current) return;

    // when the video first loads, the currentTime will be 0, and we don't want to set fileProgress to 0 in that case
    if (videoRef.current.readyState < videoRef.current.HAVE_METADATA) return;

    // once playback has cleared a corrupt spot, reset the retry schedule so any
    // later corrupt spot starts fresh with the smallest skip
    const recovery = recoveryRef.current;

    if (
      recovery.fileId !== currentFile?.id ||
      (recovery.attempts > 0 &&
        // readyState guard: timeupdate fires when currentTime is *set* (before the
        // target frame decodes), so a recovery seek alone must not count as cleared
        e.currentTarget.readyState >= e.currentTarget.HAVE_CURRENT_DATA &&
        e.currentTarget.currentTime > recovery.failTime + RECOVERY_CLEARED_SECONDS)
    ) {
      recovery.fileId = '';
      recovery.failTime = 0;
      recovery.attempts = 0;
    }

    setFileProgress(e.currentTarget.currentTime);
  };

  useEffect(() => {
    if (!videoRef.current) {
      setLoading(true);

      return;
    }

    if (videoRef.current.readyState >= videoRef.current.HAVE_CURRENT_DATA) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [videoRef.current, videoRef.current?.readyState]);

  return (
    <>
      <div
        className={cn('VideoPlayer', className)}
        ref={containerRef}
        style={{
          width: mapSize === MapSize.LARGE ? '50%' : '100%',
        }}
      >
        {isLoading && (
          <img className="VideoPlayer_Loader" src={loader} alt="Loading..." />
        )}
        <button
          type="button"
          className={cn('Button VideoSettingsButton', { 'is-active': showVideoSettings })}
          onClick={() => setShowVideoSettings(!showVideoSettings)}
        >
          Settings
        </button>
        {showVideoSettings && (
          <div ref={settingsRef}>
            <VideoSettings
              brightness={brightness}
              setBrightness={setBrightness}
              defaultBrightnessValue={1}
              contrast={contrast}
              setContrast={setContrast}
              defaultContrastValue={1}
            />
          </div>
        )}
        <button
          className="Button ScreenshotButton"
          type="button"
          onClick={snap}
          disabled={!isVideoLoaded}
        >
          Screenshot
        </button>
        <TransformWrapper
          minScale={defaultScale}
          ref={transformComponentRef}
          disablePadding
          smooth
          wheel={{ smoothStep: 0.0005 }}
          maxScale={2}
          onTransformed={e => handleScaleChange(e)}
        >
          {({ setTransform }) => {
            transformFn = setTransform;

            return (
              <div className="VideoContainer">
                <TransformComponent
                  wrapperStyle={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                  contentClass="Content"
                >
                  {files.map(file => {
                    const isCurrentVideo = file.id === currentFile?.id;

                    if (
                      (file.processingStatus !== ProcessingStatus.PROCESSED &&
                        file.processingStatus !== ProcessingStatus.ERRORED) ||
                      !file.uploadCompleted
                    )
                      return;

                    return (
                      // eslint-disable-next-line jsx-a11y/media-has-caption
                      <video
                        key={`video-${file.id}`}
                        ref={isCurrentVideo ? videoRef : undefined}
                        muted
                        onError={e => {
                          if (isCurrentVideo) handleVideoError(e, file);
                        }}
                        onLoadedMetadata={() =>
                          isCurrentVideo
                            ? getZoomState(
                                transformComponentRef.current?.state ||
                                  ({} as ReactZoomPanPinchState),
                              )
                            : {}
                        }
                        onTimeUpdate={onTimeUpdate}
                        onEnded={() => goToNextVideo(true)}
                        src={file!.url}
                        preload={getPreload(file!.id)}
                        crossOrigin="anonymous"
                        style={{
                          filter: `brightness(${brightness}) contrast(${contrast})`,
                          visibility: `${isCurrentVideo ? 'visible' : 'hidden'}`,
                          height: `${isCurrentVideo ? 'auto' : '0'}`,
                        }}
                      />
                    );
                  })}
                </TransformComponent>
              </div>
            );
          }}
        </TransformWrapper>
        {notification && (
          <NotifyComponent message={notification} type={NotifyType.ERROR} delay={8000} />
        )}
        {videoPlayerNotification && (
          <NotifyComponent
            message={videoPlayerNotification}
            type={NotifyType.ERROR}
            delay={2000}
          />
        )}
      </div>

      {showScreenshotModal && (
        <ModalShowScreen
          showModal={showScreenshotModal}
          setShowModal={setShowScreenshotModal}
          image={image}
          videoRef={videoRef}
          selectedFile={currentFile}
        />
      )}
    </>
  );
};
